/**
 * X Ads — line item rules the page enforces before anything reaches X: the backend's bidding
 * rule (checkBidding) and the targeting body replaceTargeting reads.
 */
import type { XTargetingInput } from '@/services/x-ads.service';

/** The backend's bidding rule, so the page never sends a combination X refuses. */
export function biddingProblem(objective: string, strategy: string, bidMicros: number | null): string | null {
  if (strategy === 'MAX' && ['REACH', 'FOLLOWERS', 'APP_INSTALLS'].includes(objective)) return 'Maximum bid is not available for Reach, Followers or App installs — use Automatic or Target bid.';
  if (strategy === 'TARGET' && !['REACH', 'FOLLOWERS', 'WEBSITE_CLICKS', 'APP_INSTALLS'].includes(objective)) return 'Target bid is only available for Reach, Followers, Website clicks and App installs.';
  if ((strategy === 'MAX' || strategy === 'TARGET') && !(bidMicros && bidMicros > 0)) return 'Enter a bid amount for Maximum / Target bid.';
  return null;
}
export const strategyAllowed = (objective: string, strategy: string) => biddingProblem(objective, strategy, 1) === null;

// ── What X accepts per objective (checked against the live Ads API, 17 Oct 2026, in iq-operations) ──
const ON_X = ['ALL_ON_TWITTER', 'TWITTER_REPLIES', 'TWITTER_SEARCH', 'TWITTER_TIMELINE'];
const OFF_X = ['PUBLISHER_NETWORK', 'TAP_BANNER', 'TAP_FULL', 'TAP_FULL_LANDSCAPE', 'TAP_NATIVE', 'TAP_MRECT'];
interface ObjectiveRule { goals: string[]; products: string[]; placements: string[]; app?: boolean }
/**
 * Goals X optimises for, product types and placements each objective takes.
 * Not offered: SITE_VISITS / WEBSITE_CONVERSIONS (they need a conversion tag) and the Brand TAP
 * off-network placements X discontinued for every objective but website clicks and apps.
 */
export const OBJECTIVE_RULES: Record<string, ObjectiveRule> = {
  REACH: { goals: ['MAX_REACH', 'REACH_WITH_ENGAGEMENT'], products: ['PROMOTED_TWEETS'], placements: ['ALL_ON_TWITTER', 'TWITTER_TIMELINE'] },
  ENGAGEMENTS: { goals: ['ENGAGEMENT'], products: ['PROMOTED_TWEETS'], placements: ON_X },
  VIDEO_VIEWS: { goals: ['VIDEO_VIEW', 'VIEW_3S_100PCT', 'VIEW_6S', 'VIEW_15S'], products: ['PROMOTED_TWEETS'], placements: ['ALL_ON_TWITTER', 'TWITTER_SEARCH', 'TWITTER_TIMELINE'] },
  PREROLL_VIEWS: { goals: ['PREROLL', 'PREROLL_STARTS', 'VIEW_3S_100PCT', 'VIEW_6S', 'VIEW_15S'], products: ['MEDIA', 'PROMOTED_TWEETS'], placements: ['ALL_ON_TWITTER', 'TWITTER_SEARCH', 'TWITTER_TIMELINE'] },
  WEBSITE_CLICKS: { goals: ['LINK_CLICKS'], products: ['PROMOTED_TWEETS'], placements: [...ON_X, ...OFF_X] },
  APP_INSTALLS: { goals: ['APP_INSTALLS', 'APP_CLICKS', 'APP_PURCHASES'], products: ['PROMOTED_TWEETS', 'MEDIA'], placements: [...ON_X, ...OFF_X], app: true },
  APP_ENGAGEMENTS: { goals: ['APP_CLICKS'], products: ['PROMOTED_TWEETS', 'MEDIA'], placements: ['ALL_ON_TWITTER', 'TWITTER_SEARCH', 'TWITTER_TIMELINE', ...OFF_X], app: true },
  FOLLOWERS: { goals: ['FOLLOWERS'], products: ['PROMOTED_ACCOUNT'], placements: ['ALL_ON_TWITTER', 'TWITTER_PROFILE', 'TWITTER_SEARCH', 'TWITTER_TIMELINE'] },
};
const ruleOf = (objective: string): ObjectiveRule => OBJECTIVE_RULES[objective] ?? { goals: [], products: ['PROMOTED_TWEETS'], placements: ['ALL_ON_TWITTER'] };
export const goalsFor = (objective: string) => ruleOf(objective).goals;
export const productsFor = (objective: string) => ruleOf(objective).products;
/** The MEDIA product of the app objectives runs on "All on X" and the publisher network only. */
export const placementsFor = (objective: string, product = '') => {
  const all = ruleOf(objective).placements;
  return product === 'MEDIA' && ruleOf(objective).app ? all.filter((p) => p === 'ALL_ON_TWITTER' || OFF_X.includes(p)) : all;
};
/**
 * Ticking a placement drops the ones X will not pair with it: "All on X" already covers the
 * single X placements, and single X placements cannot go with publisher network ones.
 */
export function togglePlacement(current: string[], p: string, on: boolean): string[] {
  if (!on) return current.filter((x) => x !== p);
  const single = (x: string) => x.startsWith('TWITTER_');
  let next = current.filter((x) => x !== p);
  if (p === 'ALL_ON_TWITTER') next = next.filter((x) => !single(x));
  else if (single(p)) next = next.filter((x) => x !== 'ALL_ON_TWITTER' && !OFF_X.includes(x));
  else if (OFF_X.includes(p)) next = next.filter((x) => !single(x));
  return [...next, p];
}
export const needsApp = (objective: string) => !!ruleOf(objective).app;
export const isOffNetwork = (placement: string) => OFF_X.includes(placement);
/** Pre-roll and the MEDIA product run library videos (media creatives) instead of posts. */
export const usesMediaCreatives = (objective: string, product: string) => objective === 'PREROLL_VIEWS' || product === 'MEDIA';
/** What a line item's creative must be, as X checks it when a post is promoted. */
export function creativeNeed(objective: string): string | null {
  if (objective === 'VIDEO_VIEWS') return 'Video views line items promote posts with a video.';
  if (objective === 'APP_INSTALLS' || objective === 'APP_ENGAGEMENTS') return 'App line items promote posts with an app card (Creatives → New post → App card).';
  return null;
}
/** X asks for the brand's IAB categories for pre-roll, the MEDIA product and off-network placements. */
export const needsCategories = (objective: string, product: string, placements: string[]) =>
  objective === 'PREROLL_VIEWS' || product === 'MEDIA' || placements.some(isOffNetwork);

const GOAL_LABELS: Record<string, string> = {
  MAX_REACH: 'Maximum reach', REACH_WITH_ENGAGEMENT: 'Reach with engagement', ENGAGEMENT: 'Engagement', VIDEO_VIEW: 'Video views',
  VIEW_3S_100PCT: '3-second views, fully on screen', VIEW_6S: '6-second views', VIEW_15S: '15-second views', PREROLL: 'Pre-roll views',
  PREROLL_STARTS: 'Pre-roll starts', LINK_CLICKS: 'Link clicks', APP_INSTALLS: 'App installs', APP_CLICKS: 'App clicks', APP_PURCHASES: 'App purchases',
  FOLLOWERS: 'Followers',
};
export const goalLabel = (g: string) => GOAL_LABELS[g] ?? g.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
const PRODUCT_LABELS: Record<string, string> = {
  PROMOTED_TWEETS: 'Promoted posts', PROMOTED_ACCOUNT: 'Promoted account (follower ads)', MEDIA: 'Media (in-stream video and X Audience Platform)',
};
export const productLabel = (p: string) => PRODUCT_LABELS[p] ?? p;

/** "ALL_ON_TWITTER" → "All on X", "TWITTER_SEARCH" → "X search", "TAP_FULL" → "Tap full". */
export function placementLabel(p: string): string {
  if (p === 'ALL_ON_TWITTER') return 'All on X';
  if (p === 'PUBLISHER_NETWORK') return 'Publisher network';
  const s = p.replace(/^TWITTER_/, 'X_').replace(/_/g, ' ').toLowerCase();
  return s.replace(/^x /, 'X ').replace(/^./, (c) => c.toUpperCase());
}

/** The line item form after its objective changes: product, goal and placements X accepts for it. */
export function fitToObjective<T extends { objective: string; productType: string; goal: string; placements: string[]; bidStrategy: string }>(form: T, objective: string): T {
  const products = productsFor(objective);
  const productType = products.includes(form.productType) ? form.productType : products[0];
  const placements = form.placements.filter((p) => placementsFor(objective, productType).includes(p));
  return {
    ...form,
    objective,
    productType,
    goal: goalsFor(objective).includes(form.goal) ? form.goal : '',
    placements: placements.length ? placements : ['ALL_ON_TWITTER'],
    bidStrategy: strategyAllowed(objective, form.bidStrategy) ? form.bidStrategy : 'AUTO',
  };
}

// ── Targeting ────────────────────────────────────────────────────────────────
export interface Opt { value: string; label: string; hint?: string }
/** X: exact keywords cannot go on promoted posts, and only phrase / unordered keywords can be excluded. */
export type MatchType = 'BROAD' | 'PHRASE' | 'UNORDERED';
export const matchTypesFor = (exclude: boolean): MatchType[] => (exclude ? ['PHRASE', 'UNORDERED'] : ['BROAD', 'PHRASE', 'UNORDERED']);
export interface Keyword { text: string; matchType: MatchType; exclude: boolean }

export interface TargetingState {
  locations: Opt[];
  languages: Opt[];
  interests: Opt[];
  platforms: string[];
  gender: '' | '1' | '2';
  ageBucket: string;
  keywords: Keyword[];
  customAudiences: Opt[];
  excludedCustomAudiences: Opt[];
}

export const emptyTargeting = (): TargetingState => ({
  locations: [], languages: [], interests: [], platforms: [], gender: '', ageBucket: '', keywords: [], customAudiences: [], excludedCustomAudiences: [],
});

const values = (opts: Opt[]) => opts.map((o) => o.value);

/** The body of POST line-items `targeting` / PUT line-items/{id}/targeting; empty parts are left out. */
export function toTargetingInput(t: TargetingState): XTargetingInput {
  const out: XTargetingInput = {};
  if (t.locations.length) out.locations = values(t.locations);
  if (t.languages.length) out.languages = values(t.languages);
  if (t.interests.length) out.interests = values(t.interests);
  if (t.platforms.length) out.platforms = t.platforms;
  if (t.gender) out.gender = t.gender;
  if (t.ageBucket) out.ageBucket = t.ageBucket;
  if (t.keywords.length) out.keywords = t.keywords.map((k) => ({ text: k.text, matchType: k.matchType, exclude: k.exclude }));
  if (t.customAudiences.length) out.customAudiences = values(t.customAudiences);
  if (t.excludedCustomAudiences.length) out.excludedCustomAudiences = values(t.excludedCustomAudiences);
  return out;
}

export const targetingCount = (t: XTargetingInput): number =>
  (t.locations?.length ?? 0) + (t.languages?.length ?? 0) + (t.interests?.length ?? 0) + (t.platforms?.length ?? 0)
  + (t.gender ? 1 : 0) + (t.ageBucket ? 1 : 0) + (t.keywords?.length ?? 0) + (t.customAudiences?.length ?? 0) + (t.excludedCustomAudiences?.length ?? 0);
